import { useQuery } from 'react-query'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { getInstanceType, InstanceType, getQueryParms } from './utils'
import { STRIPE_PUBLISHABLE_KEYS } from '../constants'

const params = getQueryParms<PaymentLinkQueryParams>()

async function getCurrentUser() {
  if (params.c) {
    return Promise.resolve({ name: '', email: '' })
  } else {
    const response = await fetch('/api/users/currentUser')
    const user = await response.json()
    return user
  }
}

async function getCurrentCustomer() {
  const response = await fetch('/api/customers/current')
  const customer = await response.json()

  return customer
}

async function getInvoices() {
  const response = await fetch('/api/invoices')
  const invoices = (await response.json()) as Invoice[]

  return invoices
}

async function getPayments() {
  const response = await fetch('/api/payments')
  const payments = (await response.json()) as Payment[]

  return payments
}

async function getInvoice(invoiceId: string) {
  const response = await fetch(`/api/invoices/${invoiceId}`)
  const invoice = await response.json()

  return invoice
}

async function getAssociatedUsers() {
  const response = await fetch('/api/users/associatedUsers')
  const users = await response.json()

  return users
}

async function getStripe() {
  const key =
    getInstanceType() === InstanceType.Prd
      ? STRIPE_PUBLISHABLE_KEYS.LIVE
      : STRIPE_PUBLISHABLE_KEYS.DEV

  const stripe = await loadStripe(key)

  return stripe
}

async function getStripePaymentIntent(invoiceId: string, instanceType: string) {
  const response = await fetch('/api/payments/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      invoiceId,
      instanceType,
    }),
  })

  const intent = await response.json()

  return intent
}

async function getPaymentLink(paymentLinkCode: string) {
  const response = await fetch(`/api/paymentlinks/${paymentLinkCode}`)
  const paymentLink = await response.json()

  return paymentLink
}

// TOOD: These should live closer to the functions that they actually call to
// fetch data

export const useCurrentUser = () =>
  useQuery('currentUser', getCurrentUser, {
    refetchOnMount: false,
    staleTime: Infinity,
    retry: false,
  })

export const useCurrentCustomer = () =>
  useQuery('currentCustomer', getCurrentCustomer, {
    refetchOnMount: false,
    staleTime: Infinity,
    retry: false,
  })

export const useInvoices = () =>
  useQuery('invoices', getInvoices, {
    refetchOnMount: false,
    staleTime: Infinity,
  })

export const useInvoice = (invoiceId: string) =>
  useQuery(['invoice', invoiceId], () => getInvoice(invoiceId), {
    refetchOnMount: false,
    staleTime: Infinity,
  })

export const usePayments = () =>
  useQuery('payments', getPayments, {
    refetchOnMount: false,
    staleTime: Infinity,
  })

export const useAssociatedUsers = () =>
  useQuery('associatedUsers', getAssociatedUsers, {
    refetchOnMount: false,
    staleTime: Infinity,
  })

export const useStripe = () =>
  useQuery<Stripe>('stripe', getStripe, {
    refetchOnMount: false,
    staleTime: Infinity,
  })

export const useStripePaymentIntent = (
  invoiceId: string,
  instanceType: string,
) =>
  useQuery(
    ['stripePaymentIntent', invoiceId, instanceType],
    () => getStripePaymentIntent(invoiceId, instanceType),
    {
      refetchOnMount: false,
      staleTime: Infinity,
    },
  )

export const usePaymentLink = (paymentLinkCode: string) => {
  return useQuery<PaymentLinkData>(
    ['paymentLink', paymentLinkCode],
    () => getPaymentLink(paymentLinkCode),
    {
      refetchOnMount: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
    },
  )
}
